import type { LoadProgress } from "./types";

/** Downloads a runtime asset, reporting progress (for "Downloading Python… 40%"). */
export async function fetchWithProgress(
  url: string,
  label: string,
  onProgress: (progress: LoadProgress) => void,
  fetchImpl: typeof fetch = fetch,
): Promise<ArrayBuffer> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`failed to download ${label} (${response.status})`);
  const total = Number(response.headers.get("content-length")) || 0;
  if (!response.body) {
    const buffer = await response.arrayBuffer();
    onProgress({ label, loaded: buffer.byteLength, total: buffer.byteLength });
    return buffer;
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress({ label, loaded, total });
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress({ label, loaded, total });
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out.buffer;
}

/** "Downloading Python… 40%" (or a byte count when the size is unknown). */
export function describeProgress({ label, loaded, total }: LoadProgress): string {
  if (total > 0)
    return `Downloading ${label}… ${Math.min(100, Math.round((loaded / total) * 100))}%`;
  return `Downloading ${label}… ${(loaded / 1024 / 1024).toFixed(1)} MB`;
}
